import * as machina from "machina";
import { WindowMode } from "@teaming/common-enums";
import { getHeartbeatInterval, onWindowModeChange } from "./windowVisibility";
import messageMachine from "./messageMachine";
import debugFactory from "debug";

const debug = debugFactory( "teaming:common-io:fsm:message-machine" );
messageMachine.on( "*", debug );
const socketEventsToRegister = [
	"message",
	"close",
	"open",
	"error",
];

/**
 * The socketMachine FSM manages the websocket connection for the app.
 * It will attempt to re-connect at regular intervals until a connection
 * has been established - but it will bail if the user's creds have expired
 * or if a future caller intentionally transitions the FSM to detached state.
 *
 * The companion "./windowVisibility.js" module listens for events that indicate
 * that the window has become visible or invisible to the user. Based on this, we'll
 * change how quickly we attempt to reconnect if we're not connected, AND we'll
 * attempt a heartbeat check - which for now relies on the native WebSocket readyState
 * property (we can attempt to send a heartbeat msg if this proves insufficient).
 */
export default function socketMachineFactory( { apiSocketEndpoint, authService, } ) {
	return new machina.Fsm( {
		namespace: "socket-machine",

		initialize() {
			this.socketConnection = null;
			this.timer = null;
			onWindowModeChange( windowMode => {
				// If the user has switched back to the Teaming window, we want to
				// prompt the FSM to verify that our socket connection is still active
				if ( windowMode === WindowMode.ACTIVE ) {
					this.handle( "heartbeat" );
				}
			} );
		},

		initialState: "uninitialized",

		states: {
			uninitialized: {
				connect() {
					this.deferUntilTransition( "connecting" );
					this.transition( "connecting" );
				},
			},
			connecting: {
				async connect() {
					if ( authService.isAuthenticated() ) {
						const token = await authService.getIdToken();
						this.socketConnection = new WebSocket( `${ apiSocketEndpoint }?token=${ token }` );
						socketEventsToRegister.forEach( e => {
							this.socketConnection.addEventListener( e, ( ...args ) => {
								this.handle( e, ...args );
							} );
						} );
					} else {
						this.transition( "waitingOnAuth" );
					}
				},
				open: "connected",
				close: "disconnecting",
				error: "disconnecting",
				disconnect: "disconnecting",
			},
			connected: {
				_child: messageMachine,
				message( ...args ) {
					this.emit( "message", ...args );
				},
				heartbeat() {
					if ( this.socketConnection.readyState !== 1 ) {
						this.transition( "disconnecting" );
					}
				},
				close: "disconnecting",
				error: "disconnecting",
				disconnect: "disconnecting",
			},
			disconnecting: {
				_onEnter() {
					try {
						this.socketConnection.close();
					} catch {
						// this block left intentionally blank
					}
					// IIRC, the removeEventListener method is broken in some implementations
					// so, switching to onmessage, onerror, etc. here to unsubscribe listeners
					socketEventsToRegister.forEach( e => {
						this.socketConnection[ `on${ e }` ] = null;
					} );
					this.socketConnection = null;
					// transitioning from here would be a synchronous operation. While it wouldn't
					// break anything, I prefer to force the transition to happen on the next tick.
					// This prevents confusion if we're watching FSM events and/or console logging
					setImmediate( () => {
						this.transition( "disconnected" );
					} );
				},
			},
			disconnected: {
				_onEnter() {
					this.timer = setTimeout( () => {
						this.handle( "connect" );
					}, getHeartbeatInterval() );
				},
				connect() {
					this.deferUntilTransition( "connecting" );
					this.transition( "connecting" );
				},
				heartbeat() {
					// if we're attempting a heartbeat check while in
					// disconnected state we need to just reconnect
					this.handle( "connect" );
				},
				_onExit() {
					clearTimeout( this.timer );
				},
			},
			waitingOnAuth: {
				connect() {
					this.deferUntilTransition( "connecting" );
					this.transition( "connecting" );
				},
			},
			// This state is for intentionally taking the FSM offline (primarily for diagnostics/testing)
			// and for scenarios where the FSM attempts to connect, but the user's creds have expired
			detached: {
				_onEnter() {
					this.emit( "detached" );
				},
			},
		},

		connect() {
			this.handle( "connect" );
		},

		disconnect() {
			this.handle( "disconnect" );
		},

		pauseMessageTraffic() {
			this.handle( "pause" );
		},

		discardMessageTraffic() {
			this.handle( "ignore" );
		},

		resumeMessageTraffic() {
			this.handle( "resume" );
		},
	} );
}
